// Lib
import React, { ReactElement } from 'react';
import { createSelector, createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { cloneDeep } from 'lodash/fp';

// Utils
import logger from '../../../../../../logger/logger';
import http from '../../../../../../utils/services/http';
import { propIn } from '../../../../../../../common/utils/immutableHelper';
import { getPromptMethod } from '../promptShortcutUtils';
import { getUserSegment } from '../../../../../../../common/users/utils/userPropertyUtils';

// Actions
import { asyncResource } from '../../../../../../utils/services/http/asyncResource/asyncResource';

// Types
import { PromptShortcut } from '../../../../../../../common/ai/aiPromptShortcutTypes';
import { ResourceTypes } from '../../../../../../utils/services/http/asyncResource/asyncResourceConstants';
import { CmsEntities } from '../../../../../../cms/cmsConstants';

interface UseFetchAiToolPromptShortcutsDecoratorProps {
    currentUser: unknown;
    rootPromptShortcut: PromptShortcut;
    dispatchFetchAiAssistantShortcuts: Function;
}

/**
 * Adds the `getPromptMethod` function to each of the prompt shortcuts.
 */
export const enrichPromptShortcutMethods = (promptShortcut: PromptShortcut): PromptShortcut => {
    if (promptShortcut.getPromptMethodName) {
        promptShortcut.getPromptMethod = getPromptMethod(promptShortcut.getPromptMethodName);
    }

    const children = promptShortcut.children || [];
    children.forEach(enrichPromptShortcutMethods);

    return promptShortcut;
};

const fetchAiAssistantShortcuts = (userSegment: string) => (dispatch: Function) =>
    dispatch(
        asyncResource(
            ResourceTypes.cms,
            CmsEntities.AI_ASSISTANT_SHORTCUTS,
        )(async () => {
            try {
                const { data } = await http({ url: 'cms/ai/assistant-shortcuts', params: { segment: userSegment } });

                const rootPrompt = data.prompt;

                return rootPrompt;
            } catch (error) {
                logger.error('Error loading assistant shortcuts', error);

                return {
                    id: 'root-error',
                    children: [],
                    isRoot: true,
                };
            }
        }),
    );

const getEnhancedRootPromptShortcut = createSelector(
    propIn(['app', 'cms', 'ai-assistant-shortcuts', 'data']),
    (rootPromptShortcut: PromptShortcut) => {
        if (!rootPromptShortcut) return rootPromptShortcut;

        const clonedRootPromptShortcut = cloneDeep(rootPromptShortcut);
        return enrichPromptShortcutMethods(clonedRootPromptShortcut);
    },
);

const mapStateToProps = createStructuredSelector({
    rootPromptShortcut: getEnhancedRootPromptShortcut,
});

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchFetchAiAssistantShortcuts: (userSegment: string) => dispatch(fetchAiAssistantShortcuts(userSegment)),
});

export default (DecoratedComponent: Function): Function => {
    const UseFetchAiToolPromptShortcutsDecorator = (
        props: UseFetchAiToolPromptShortcutsDecoratorProps,
    ): ReactElement => {
        const { currentUser, dispatchFetchAiAssistantShortcuts } = props;

        React.useEffect(() => {
            const currentUserSegment = getUserSegment(currentUser);

            dispatchFetchAiAssistantShortcuts(currentUserSegment);
        }, []);

        return <DecoratedComponent {...props} />;
    };

    return connect(mapStateToProps, mapDispatchToProps)(UseFetchAiToolPromptShortcutsDecorator);
};
